import { IDateRange } from "../models";
import { IProductMeta } from "../models/product";
import { IProfile } from "../models/profile";
import { DEFAULT_META, LIMIT } from "../utils/constants.ts";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useState,
  useEffect,
} from "react";
import { useProfileQuery } from "../hooks/useProfileQuery";
import { ACCESS_TOKEN } from "../constants/auth";

export const ProfileContext = createContext<{
  profile: IProfile | undefined;
  profileLoading: boolean;
  setProfile: Dispatch<SetStateAction<IProfile | undefined>>;
  setProfileLoading: Dispatch<SetStateAction<boolean>>;
  currentPage: number;
  currentLimit: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setCurrentLimit: Dispatch<SetStateAction<number>>;
  productsMeta: IProductMeta;
  setProductsMeta: Dispatch<SetStateAction<IProductMeta>>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  filterValues: Record<string, any>;
  setFilterValues: Dispatch<SetStateAction<Record<string, any>>>;
  dateRange: IDateRange;
  setDateRange: Dispatch<SetStateAction<IDateRange>>;
}>({
  profile: undefined,
  profileLoading: false,
  setProfile: () => {},
  setProfileLoading: () => {},
  currentPage: 1,
  currentLimit: LIMIT,
  setCurrentPage: () => {},
  setCurrentLimit: () => {},
  productsMeta: DEFAULT_META,
  setProductsMeta: () => {},
  searchValue: "",
  setSearchValue: () => {},
  filterValues: {},
  setFilterValues: () => {},
  dateRange: { from: "", to: "" },
  setDateRange: () => {},
});

const ProfileContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [profile, setProfile] = useState<IProfile | undefined>();
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(LIMIT);
  const [productsMeta, setProductsMeta] = useState<IProductMeta>(DEFAULT_META);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [dateRange, setDateRange] = useState<IDateRange>({
    from: "",
    to: "",
  });

  const isLoggedIn = localStorage.getItem(ACCESS_TOKEN) !== null;
  const { data: profileData, isLoading } = useProfileQuery({
    enabled: isLoggedIn,
  });

  useEffect(() => {
    if (profileData) {
      setProfile(profileData as IProfile);
    }
  }, [profileData]);

  useEffect(() => {
    setProfileLoading(isLoading);
  }, [isLoading]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        setProfile,
        profileLoading,
        setProfileLoading,
        currentPage,
        currentLimit,
        setCurrentPage,
        setCurrentLimit,
        productsMeta,
        setProductsMeta,
        searchValue,
        setSearchValue,
        filterValues,
        setFilterValues,
        dateRange,
        setDateRange,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContextProvider;
